"use client";

import { useCallback, useEffect, useRef } from "react";
import { useAuthStore } from "@/stores/auth-store";
import { useProfileStore } from "@/stores/profile-store";
import { restoreSessionWithRetry } from "@/lib/auth/session-restore";
import { isDefinitiveInvalidRefreshTokenError } from "@/lib/auth/refresh-token-errors";
import { isAuthBackoffActive, hasStoredRefreshToken } from "@/lib/supabase/client";
import { loadPlugin } from "@/lib/capacitor/load-plugin";

/**
 * Component that syncs Supabase session with auth store on app load
 * This ensures users stay logged in across page refreshes and app restarts
 * 
 * CRITICAL: This runs early to restore session before UI renders
 * Prevents "signed out" flicker during app startup
 */
export function SessionSync() {
  const syncSession = useAuthStore((state) => state.syncSession);
  const hasRestoredRef = useRef(false);
  const syncInFlightRef = useRef<Promise<void> | null>(null);
  const lastSyncAtRef = useRef(0);

  const requestSessionSync = useCallback(
    async (reason: string, minIntervalMs = 1500) => {
      if (isAuthBackoffActive()) return;
      const now = Date.now();
      if (syncInFlightRef.current) {
        await syncInFlightRef.current;
        return;
      }
      if (now - lastSyncAtRef.current < minIntervalMs) {
        return;
      }

      const syncPromise = syncSession()
        .catch((error) => {
          console.warn("[SessionSync] Coalesced sync failed:", { reason, error });
        })
        .finally(() => {
          syncInFlightRef.current = null;
        });

      syncInFlightRef.current = syncPromise;
      lastSyncAtRef.current = now;
      await syncPromise;
    },
    [syncSession],
  );

  useEffect(() => {
    // Only restore once per app load
    if (hasRestoredRef.current) {
      return;
    }
    hasRestoredRef.current = true;
    
    // CRITICAL: Early session restoration on app load
    // This runs IMMEDIATELY and SYNCHRONOUSLY where possible to prevent flicker
    // Must complete before any UI renders or auth checks
    const performEarlyRestore = async () => {
      try {
        if (isAuthBackoffActive()) return;

        // CRITICAL: Skip restoration when user intentionally logged out
        const { isIntentionalLogout, clearIntentionalLogoutFlag } = await import("@/lib/auth/logout-flag");
        if (isIntentionalLogout()) {
          clearIntentionalLogoutFlag();
          return;
        }
        // CRITICAL: Check if offline - skip session restoration if offline
        // This prevents hanging when network is unavailable
        const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
        if (isOffline) {
          // Use persisted Zustand state when offline
          return;
        }

        // CRITICAL: If no token in storage (web), skip waiting for restoration - settle to signed-out immediately.
        // On Capacitor we must wait for Preferences to load before we can know.
        const isCapacitor = typeof (window as any).Capacitor !== "undefined";
        if (!isCapacitor && !hasStoredRefreshToken()) {
          return;
        }

        // CRITICAL: First, wait for Supabase session restoration to complete
        // This ensures Capacitor Preferences tokens are loaded before we check.
        // If it doesn't settle quickly, defer session sync instead of continuing
        // into "logged out" checks while restoration is still in-flight.
        if (typeof window !== "undefined") {
          try {
            const { sessionRestorePromise } = await import("@/lib/supabase/client");
            let restorationSettled = false;
            await Promise.race([
              sessionRestorePromise.then(() => {
                restorationSettled = true;
              }),
              new Promise<void>((resolve) => setTimeout(resolve, 5000)),
            ]);

            if (!restorationSettled) {
              sessionRestorePromise
                .then(() => requestSessionSync("early-restore.deferred-after-timeout", 0))
                .catch(() => {});
              return;
            }
          } catch {
            // Non-fatal - continue
          }
        }
        
        // CRITICAL: Log session boot BEFORE any redirects or checks
        const { logSessionBoot } = await import("@/lib/debug/session-logger");
        logSessionBoot({
          phase: "early-restore",
          timestamp: Date.now(),
        });
        
        // CRITICAL: First, check persisted auth state from Zustand
        // This is the fastest restoration method and preserves state even if Supabase check fails
        const persistedAuth = useAuthStore.getState();
        if (persistedAuth.user && persistedAuth.isAuthenticated) {
          // We have persisted auth - validate it with Supabase but don't clear if validation fails
          // Validation might fail due to network issues, but session could still be valid
          try {
            const { supabase } = await import("@/lib/supabase/client");
            const { data: { session }, error } = await supabase.auth.getSession();
            
            if (session?.access_token && !error) {
              await requestSessionSync("early-restore.persisted-validation");
              return;
            } else if (error) {
              const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(error);
              
              // CRITICAL: Only clear on explicit invalid token errors, not network issues
              const isNetworkError = error.message?.includes("network") ||
                                     error.message?.includes("timeout") ||
                                     error.message?.includes("fetch");
              
              if (isInvalidTokenError && !isNetworkError) {
                const { logSignOutCall } = await import("@/lib/debug/session-logger");
                logSignOutCall("SessionSync.invalidToken", "Invalid refresh token error");
                await useAuthStore.getState().clearClientAuthSession();
                return;
              }
              
              // For network errors and other non-fatal issues, preserve persisted state
              return;
            } else {
              // No Supabase session - only clear if we're certain there's no token (desync)
              if (!hasStoredRefreshToken()) {
                useAuthStore.setState({ user: null, isAuthenticated: false, membershipTier: "free" });
                useProfileStore.setState({ currentProfile: null });
              }
              return;
            }
          } catch {
            // Validation failed - preserve persisted state anyway
            return;
          }
        }
        
        // No persisted auth state - check Supabase directly
        if (typeof window !== "undefined") {
          const { supabase } = await import("@/lib/supabase/client");
          
          // CRITICAL: Read session from localStorage immediately
          // This is the primary method for session restoration on app/browser reopen
          // Supabase client should have already restored session on boot, but verify here
          const { data: { session: existingSession }, error: sessionError } = await supabase.auth.getSession();
          
          // Log session result with comprehensive details
          const { logSessionResult } = await import("@/lib/debug/session-logger");
          logSessionResult(existingSession, {
            phase: "early-restore-direct-check",
            hasError: !!sessionError,
            errorMessage: sessionError?.message,
            timestamp: Date.now(),
          });
          
          if (existingSession?.access_token && !sessionError) {
            // Log session restore event
            try {
              const { logLoginEvent } = await import("@/lib/debug/session-logger");
              logLoginEvent({
                method: "session-restore",
                userId: existingSession.user?.id,
                source: "SessionSync-early-restore",
                timestamp: Date.now(),
              });
            } catch {
              // Logging is non-critical
            }
            
            await requestSessionSync("early-restore.supabase-session");
            return;
          } else if (sessionError) {
            // CRITICAL: Only clear on explicit invalid token errors, not network issues
            const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(sessionError);
            const isNetworkError = sessionError.message?.includes("network") ||
                                   sessionError.message?.includes("timeout") ||
                                   sessionError.message?.includes("fetch");
            
            if (isInvalidTokenError && !isNetworkError) {
                const { logSignOutCall } = await import("@/lib/debug/session-logger");
                logSignOutCall("SessionSync.invalidTokenStorage", "Invalid refresh token in storage");
                await useAuthStore.getState().clearClientAuthSession();
                return;
              }
            // For network errors and other non-fatal issues, preserve session
            // and try to continue with full restoration
          }
        }
        
        // If no session found, try full restoration
        const result = await restoreSessionWithRetry();
        
        if (!result.success && !result.requiresLogin) {
          // Inconclusive - non-fatal, session may still work
        }
      } catch (error) {
        console.error("[SessionSync] Error during early session restoration:", error);
        // Even if restoration fails, try to sync session (might still work)
        try {
          await requestSessionSync("early-restore.catch-fallback");
        } catch (syncError) {
          console.error("[SessionSync] Session sync also failed:", syncError);
        }
      }
    };
    
    // Run early restoration immediately
    performEarlyRestore();
  }, [requestSessionSync]);

  useEffect(() => {
    // Sync session on mount (skipped when auth backoff active)
    if (!isAuthBackoffActive()) {
      requestSessionSync("mount-initial-sync");
    }

    // Auth state changes are handled by the singleton listener in `lib/supabase/client`.
    // Avoid adding another subscription here to prevent duplicate refresh/sync loops.
  }, [requestSessionSync]);

  // CRITICAL: Handle Capacitor app resume to restore session
  // This ensures users stay logged in when reopening the app
  useEffect(() => {
    if (typeof window === "undefined") return;
    
    // Check if we're in Capacitor
    const isCapacitor = typeof (window as any).Capacitor !== "undefined" || 
                       (window as any).Capacitor?.isNativePlatform?.() === true;
    if (!isCapacitor) return;
    
    let resumeListener: any = null;
    
    const setupResumeListener = async () => {
      try {
        /*
         * ASK WHETHER THIS INSTALLED APP HAS THE PLUGIN, DO NOT JUST IMPORT IT.
         *
         * Importing @capacitor/app always succeeds: the JavaScript half ships in the web bundle,
         * which every installed app downloads fresh from growth-sync.com. The NATIVE half is
         * whatever build that person installed, possibly months old. So the import resolves, and
         * then the first call throws
         *
         *     Error: "App" plugin is not implemented on android
         *
         * This was the last call site in the codebase still doing it the unguarded way. The other
         * four (deep links, push, badges, login) were moved onto loadPlugin on 2026-09-11 after
         * exactly this error hit the landing page eleven times in five minutes; see the header of
         * lib/capacitor/load-plugin.ts. loadPlugin asks Capacitor.isPluginAvailable("App") first
         * and returns null rather than handing back something that throws when touched.
         */
        const mod = await loadPlugin("App", () => import("@capacitor/app"));
        if (!mod?.App) return; // older build with no App plugin: no resume listener
        const { App } = mod;
        
        resumeListener = await App.addListener("appStateChange", async (state: any) => {
          if (state.isActive) {
            // App became active - restore session from Capacitor Preferences
            try {
              const { Preferences } = await import("@capacitor/preferences");
              const { supabase } = await import("@/lib/supabase/client");
              
              const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || "";
              const projectRef = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
              const authTokenKey = projectRef ? `sb-${projectRef}-auth-token` : null;
              
              if (authTokenKey) {
                const { value } = await Preferences.get({ key: authTokenKey });
                
                if (value) {
                  window.localStorage.setItem(authTokenKey, value);
                  const { data: { session }, error } = await supabase.auth.getSession();
                  
                  if (session?.access_token) {
                    await requestSessionSync("capacitor-resume.valid-session");
                  } else if (error) {
                    await requestSessionSync("capacitor-resume.verify-error");
                  }
                } else {
                  await requestSessionSync("capacitor-resume.no-preferences-token");
                }
              } else {
                await requestSessionSync("capacitor-resume.no-token-key");
              }
            } catch (resumeError) {
              console.error("[SessionSync] Error restoring session on app resume:", resumeError);
              try {
                await requestSessionSync("capacitor-resume.fallback");
              } catch {
                // Non-fatal
              }
            }
          }
        });
      } catch {
        // Gracefully handle if Capacitor App is not available (web builds)
      }
    };
    
    setupResumeListener();
    
    return () => {
      // try/catch as well as .catch(): on a plugin the installed app does not carry, Capacitor
      // throws synchronously from the call itself, so .catch() is never reached and the error
      // escapes out of a React cleanup function.
      if (resumeListener) {
        try {
          const removed = resumeListener.remove?.();
          if (removed && typeof removed.catch === "function") removed.catch(() => {});
        } catch {
          // Nothing to remove, which is the same outcome as removing it.
        }
      }
    };
  }, [requestSessionSync]);

  return null; // This component doesn't render anything
}

